import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import axios from "axios";
import { queueOfflineAgent } from "../../../../services/pathologyOutbox";

const PathologyAgentModal = ({ isOpen, onClose, onSave, agentToEdit }) => {
  const isEditMode = !!agentToEdit;
  const API_BASE_URL = "https://api.muktihospital.com/api/pathology-agents";
  const API_KEY = "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

  const [agentData, setAgentData] = useState({
    name: "",
    designation: "",
    contactNo: "",
    address: "",
    division: "Dhaka",
    district: "Dhaka",
    upazila: "",
    union: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isOpen) {
      if (isEditMode && agentToEdit) {
        setAgentData({
          name: agentToEdit.name || "",
          designation: agentToEdit.designation || "",
          contactNo: agentToEdit.contactNo || "",
          address: agentToEdit.address || "",
          division: agentToEdit.division || "Dhaka",
          district: agentToEdit.district || "Dhaka",
          upazila: agentToEdit.upazila || "",
          union: agentToEdit.union || "",
        });
      } else {
        setAgentData({
          name: "",
          designation: "",
          contactNo: "",
          address: "",
          division: "Dhaka",
          district: "Dhaka",
          upazila: "",
          union: "",
        });
      }
      setErrors({});
    }
  }, [isOpen, agentToEdit, isEditMode]);

  const validateForm = () => {
    const newErrors = {};
    let hasError = false;

    if (!agentData.name.trim()) {
      newErrors.name = "Agent Name is required";
      hasError = true;
    }

    setErrors(newErrors);
    if (hasError) {
      toast.error("Please fill in required fields", {
        position: "top-right",
        autoClose: 3000,
      });
    }
    return !hasError;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAgentData((prevData) => ({ ...prevData, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    const payload = {
      agentName: agentData.name.trim(),
      designation: agentData.designation.trim(),
      contactNumber: agentData.contactNo.trim(),
      address: agentData.address.trim(),
      division: agentData.division.trim(),
      district: agentData.district.trim(),
      upazila: agentData.upazila.trim(),
      union: agentData.union.trim(),
    };
    try {
      let savedAgent;

      if (isEditMode) {
        const response = await axios.put(
          `${API_BASE_URL}/edit/${agentToEdit.id}`,
          payload,
          { headers: { "x-api-key": API_KEY } }
        );
        
        const updated = response.data.agent;
        savedAgent = {
          id: updated.id,
          agentCode: updated.agentCode,
          name: updated.agentName,
          designation: updated.designation,
          contactNo: updated.contactNumber,
          address: updated.address,
          division: updated.division,
          district: updated.district,
          upazila: updated.upazila,
          union: updated.union,
        };
        toast.success("Agent updated successfully!", {
          position: "top-right",
          autoClose: 2000,
        });
      } else {
        const response = await axios.post(
          `${API_BASE_URL}/add`,
          payload,
          { headers: { "x-api-key": API_KEY } }
        );
        
        const created = response.data.agent;
        savedAgent = {
          id: created.id,
          agentCode: created.agentCode,
          name: created.agentName,
          contactNo: created.contactNumber,
          address: created.address,
          division: created.division,
          district: created.district,
          upazila: created.upazila,
          union: created.union,
        };
        toast.success("Agent added successfully!", {
          position: "top-right",
          autoClose: 2000,
        });
      }

      onSave(savedAgent, isEditMode);
      onClose();
    } catch (error) {
      console.error("Error saving pathology agent:", error);
      const isNetworkError =
        error.code === "ERR_NETWORK" || !navigator.onLine;
      // Offline + creating a new agent -> queue it for later sync.
      // Edits stay online-only for now (safer; matches the bill/test pattern).
      if (isNetworkError && !isEditMode) {
        const clientId =
          typeof crypto !== "undefined" && crypto.randomUUID
            ? crypto.randomUUID()
            : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        const ok = await queueOfflineAgent(payload, clientId);
        if (ok) {
          toast.success(
            "Agent saved offline. It will sync when you are online.",
            { position: "top-right", autoClose: 2500 }
          );
          // Send a provisional agent up to the parent so the list can
          // optimistically show it, mirroring the online flow.
          onSave(
            {
              id: `offline-${clientId}`,
              agentCode: "PENDING",
              name: payload.agentName,
              designation: payload.designation,
              contactNo: payload.contactNumber,
              address: payload.address,
              division: payload.division,
              district: payload.district,
              upazila: payload.upazila,
              union: payload.union,
              __offline: true,
              clientId,
            },
            false
          );
          onClose();
          setIsLoading(false);
          return;
        }
      }
      const errorMsg = error.response?.data?.error || "Failed to save agent.";
      toast.error(errorMsg, { position: "top-right", autoClose: 3000 });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6 border-b border-gray-200">
          <h2 className="text-lg sm:text-xl font-bold text-gray-700">
            {isEditMode ? "Edit Agent" : "Add Agent"}
          </h2>
        </div>
        
        <div className="p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Agent Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Agent Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={agentData.name}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none text-sm ${
                errors.name ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter agent name"
              disabled={isLoading}
            />
            {errors.name && <span className="text-red-500 text-xs">{errors.name}</span>}
          </div>
          
          {/* ADDED DESIGNATION FIELD */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Designation (If Doctor)
            </label>
            <input
              type="text"
              name="designation"
              value={agentData.designation}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md outline-none text-sm"
              placeholder="e.g., MBBS, FCPS"
              disabled={isLoading}
            />
          </div>

          {/* Contact Number */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Contact Number
            </label>
            <input
              type="text"
              name="contactNo"
              value={agentData.contactNo}
              onChange={handleChange}
              className={`w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-gray-700 ${
                errors.contactNo ? "border-red-500" : "border-gray-300"
              }`}
              placeholder="Enter contact number"
              disabled={isLoading}
            />
          </div>

          {/* Address */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <input
              type="text"
              name="address"
              value={agentData.address}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md outline-none text-sm"
              placeholder="Enter full address"
              disabled={isLoading}
            />
          </div>

          {/* Division */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Division</label>
            <input
              type="text"
              name="division"
              value={agentData.division}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md outline-none text-sm"
              placeholder="Enter division"
              disabled={isLoading}
            />
          </div>

          {/* District */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">District</label>
            <input
              type="text"
              name="district"
              value={agentData.district}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md outline-none text-sm"
              placeholder="Enter district"
              disabled={isLoading}
            />
          </div>

          {/* Upazila */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Upazila</label>
            <input
              type="text"
              name="upazila"
              value={agentData.upazila}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md outline-none text-sm"
              placeholder="Enter upazila"
              disabled={isLoading}
            />
          </div>

          {/* Union */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Union</label>
            <input
              type="text"
              name="union"
              value={agentData.union}
              onChange={handleChange}
              className="w-full p-2 border border-gray-300 rounded-md outline-none text-sm"
              placeholder="Enter union"
              disabled={isLoading}
            />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="bg-[#E7633D]/90 text-sm text-white py-2 px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            disabled={isLoading}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            className="bg-M-primary-color/90 text-sm text-white py-2 px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            disabled={isLoading}
          >
            {isLoading ? "Saving..." : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PathologyAgentModal;